import React, {FC, useRef, useEffect, useState, useCallback} from 'react'
import {skipToken} from '@reduxjs/toolkit/query'
import {Container, Row, Col, Card, OverlayTrigger, Tooltip} from 'react-bootstrap'
import {QRCode} from 'react-qrcode-logo'
import {ENV} from '../../../../../Utils/_helpers/EnvHelper'
import PreviewWalletCard from '../PreviewWalletCard/PreviewWalletCard'
import {Link, useLocation, useNavigate, useParams} from 'react-router-dom'
import {CardData, formSettingToAPI} from '../../type'
import './WalletCardSuccessPage.scss'
import {
  useLazyGetWalletCardQuery,
  useLazyGetWalletCardTemplateDetailQuery,
} from '../../../../../../services/cellcast.v2'
import {useI18n} from '../../../../../Utils/_helpers/useI18n'
import {
  useDownloadWalletCardPublicQuery,
  useDownloadWalletCardPublicGoogleQuery,
} from '../../../../../../services/cellcast.v2'
import {useWalletCardTemplate} from '../../../wallet-card-templates/hooks/useWalletCardTemplate'
import useResponsive from '../../../../../hooks/useResponsive'

const WalletCardSuccessPage: FC = () => {
  const navigate = useNavigate()
  const {cardId} = useParams<{cardId: string}>()
  const [cardData, setCardData] = useState<CardData | null>(null)
  const isTemplate = useLocation().pathname.includes('card-templates')

  const {updateWalletCardTemplate, isUpdating} = useWalletCardTemplate()
  const {isDesktop, isMobile} = useResponsive()
  const {t} = useI18n()
  // Get wallet card query hook
  const [getWalletCard, {data: walletCardData, isLoading}] = useLazyGetWalletCardQuery()
  const [getWalletCardTemplate, {data: walletCardTemplateData, isLoading: isLoadingTemplate}] =
    useLazyGetWalletCardTemplateDetailQuery()

  // Fetch wallet card data when component mounts
  useEffect(() => {
    if (cardId) {
      isTemplate ? getWalletCardTemplate({id: cardId}) : getWalletCard({id: cardId})
    }
  }, [cardId, getWalletCard, getWalletCardTemplate, isTemplate])

  // Update cardData when API response is received
  useEffect(() => {
    if (walletCardData?.data || walletCardTemplateData?.data) {
      const data = isTemplate ? walletCardTemplateData?.data : walletCardData?.data
      setCardData(data as CardData)
    }
  }, [walletCardData, walletCardTemplateData, isTemplate])

  const qrRef = useRef<QRCode>(null)
  const qrContainerRef = useRef<HTMLDivElement>(null)
  const qrCodeUrl =
    isTemplate && cardData?.status === 'draft'
      ? `${ENV().REACT_APP_FE_URL}/dashboard`
      : `${ENV().REACT_APP_FE_URL}/wallet-card/download/${cardId}`

  /* ------------ Device Detection ------------- */
  const [deviceInfo, setDeviceInfo] = useState({isAndroid: false, isIOS: false})

  const detectDevice = useCallback(() => {
    const ua = navigator.userAgent.toLowerCase()
    setDeviceInfo({
      isAndroid: /android/i.test(ua),
      isIOS: /iphone|ipad|ipod/i.test(ua),
    })
  }, [])
  useEffect(() => {
    detectDevice()
  }, [detectDevice])

  /* ------------ Auto Download ------------ */
  const [downloadTriggered, setDownloadTriggered] = useState(false)
  // These queries will run conditionally
  const {data: pkpassBlob} = useDownloadWalletCardPublicQuery(
    downloadTriggered && deviceInfo.isIOS && cardId ? {id: cardId} : skipToken
  )
  const {data: googleWallet} = useDownloadWalletCardPublicGoogleQuery(
    downloadTriggered && deviceInfo.isAndroid && cardId ? {id: cardId} : skipToken
  )

  useEffect(() => {
    if (cardId && !downloadTriggered) {
      // Trigger download once after device detected
      setDownloadTriggered(true)
    }
  }, [cardId, deviceInfo, downloadTriggered])

  const handleBackToForm = () => {
    navigate(`/wallet-card/edit/${cardId}`, {state: {isEdit: true}})
  }

  const [isDefaultChecked, setIsDefaultChecked] = useState<boolean>(cardData?.isDefault || false)

  // set initial default checkbox based on cardData when loaded
  useEffect(() => {
    if (cardData && cardData.isDefault) {
      setIsDefaultChecked(cardData.isDefault)
    }
  }, [cardData])

  return (
    <Container className='py-3 py-md-5 wallet-card-success-page'>
      <Card className='shadow-sm border-0 mb-4 mb-md-7 interactive-card'>
        <Card.Header className='bg-white border-0 py-3 py-md-5'>
          <div className='d-flex flex-column w-100'>
            <h1 className='card-title align-items-start flex-column'>
              <div className='d-flex align-items-center gap-3 mb-2'>
                <OverlayTrigger
                  placement='right'
                  overlay={<Tooltip>Go back to wallet cards</Tooltip>}
                >
                  <span
                    className='me-2'
                    style={{cursor: 'pointer'}}
                    onClick={() => navigate(-1)}
                    title='Go back'
                  >
                    <i className='bi bi-arrow-left fs-4 text-primary'></i>
                  </span>
                </OverlayTrigger>
                <span className='card-label fw-bolder fs-2 mb-1'>
                  {isTemplate
                    ? t('MOBILE_WALLET_CARD.TEMPLATE.SUCCESS.TITLE')
                    : 'AI B-Card Details'}
                </span>
              </div>
              <span className='mt-1 fw-bold fs-7'>
                {isTemplate
                  ? t('MOBILE_WALLET_CARD.TEMPLATE.SUCCESS.DESCRIPTION')
                  : 'View your card and details.'}
              </span>
            </h1>

            {/* QR Code Section - Mobile Only */}
            {/* {!isTemplate && ( */}
              <div className='d-block d-lg-none mt-4'>
                <div className='d-flex flex-column align-items-center'>
                  <div className='d-flex flex-column mb-3'>
                    <p className='text-center fs-4 fw-bold m-0 p-0 responsive-qr-title'>
                      {t('WALLET_CARD.DETAIL.TITLE')}
                    </p>
                    <p className='text-center fs-6 responsive-qr-description'>
                      {t('WALLET_CARD.DETAIL.DESCRIPTION')}
                    </p>
                  </div>
                  <div className='bg-light p-2 rounded-3 interactive-qr-container'>
                    <div ref={qrContainerRef} className='qr-code-wrapper'>
                      <QRCode
                        ref={qrRef}
                        size={window.innerWidth <= 768 ? 200 : 250}
                        value={qrCodeUrl}
                        fgColor='#000000'
                        bgColor='#ffffff'
                      />
                    </div>
                  </div>
                </div>
              </div>
            {/* )} */}
          </div>
        </Card.Header>

        <Card.Body className='p-0'>
          {isLoading || isLoadingTemplate || !cardData ? (
            <div className='d-flex justify-content-center py-10'>
              <div className='spinner-border text-primary' role='status'>
                <span className='visually-hidden'>Loading...</span>
              </div>
            </div>
          ) : (
            <div className='px-3 px-md-5 px-lg-10 py-5 py-md-10'>
              <Row className='gx-3 gx-md-5 gx-lg-10'>
                <Col xs={12} lg={6} className='mb-6 mb-lg-0'>
                  <div className='position-relative d-flex justify-content-center preview-wallet-card'>
                    <div className='d-flex flex-column mb-3'>
                      <p className='text-start fs-1 fw-bold m-0 p-0 responsive-preview-title'>
                        {isTemplate
                          ? 'Template Preview'
                          : 'Card Preview -  Take it for a Test Drive!'}
                      </p>
                      {isTemplate ? (
                        <p className='text-start fs-5 responsive-preview-text'>
                          Review your template on all devices.
                          <br />
                          If you need to make adjustments, edit it here:{' '}
                          <Link
                            to={`/card-templates/edit/${cardId}`}
                            state={{isEdit: true, isTemplate: true}}
                            className='interactive-link'
                          >
                            Edit Template
                          </Link>
                        </p>
                      ) : (
                        <p className='text-start fs-5 responsive-preview-text'>
                          Scan the QR code on your card below to test it out just like your customer
                          would.
                          <br />
                          If you need to make adjustments, edit it here:{' '}
                          <span className='text-edit interactive-link' onClick={handleBackToForm}>
                            {t('WALLET_CARD.BUTTON.BACK_TO_FORM')}
                          </span>
                        </p>
                      )}
                    </div>
                    <PreviewWalletCard
                      cardData={cardData}
                      disableInput={true}
                      disableStepSync={true}
                    />
                  </div>
                </Col>
                {isTemplate ? (
                  <Col
                    xs={12}
                    lg={6}
                    className='d-flex flex-column align-items-center mt-4 mt-lg-8'
                  >
                    <div className='template-success-wrapper w-100 d-flex flex-column align-items-start bg-light-primary rounded interactive-panel'>
                      <h3 className='fw-bold responsive-panel-title'>
                        {t('MOBILE_WALLET_CARD.TEMPLATE.SUCCESS_TITLE')}
                      </h3>
                      <p className='fs-6 mt-1 fw-bold responsive-panel-description'>
                        {t('MOBILE_WALLET_CARD.TEMPLATE.SUCCESS_DESCRIPTION')}
                      </p>

                      {/* Default template checkbox */}
                      <label className='form-check-label interactive-label'>
                        {t('MOBILE_WALLET_CARD.TEMPLATE.SET_DEFAULT')}
                      </label>
                      <div className='form-check form-switch mt-2 mb-4 interactive-switch'>
                        <input
                          className='form-check-input h-20px w-40px interactive-checkbox'
                          type='checkbox'
                          id='setDefaultTemplateChk'
                          checked={isDefaultChecked}
                          disabled={isUpdating || cardData?.status === 'draft'}
                          onChange={async (e) => {
                            const newVal = e.target.checked
                            setIsDefaultChecked(newVal)
                            if (cardId) {
                              try {
                                await updateWalletCardTemplate({
                                  id: cardData.id,
                                  type: cardData.type,
                                  organizationName: cardData.organizationName,
                                  cardName: cardData.cardName,
                                  fullName: cardData.fullName,
                                  phone: cardData.phone,
                                  email: cardData.email,
                                  shareMethod: cardData.shareMethod,
                                  formSettings: formSettingToAPI(cardData.formSettings),
                                  isDefault: newVal,
                                } as any)
                              } catch (err) {
                                // revert on error
                                setIsDefaultChecked(!newVal)
                              }
                            }
                          }}
                        />
                        <OverlayTrigger
                          placement='top'
                          overlay={
                            <Tooltip id='tooltip'>
                              {t('MOBILE_WALLET_CARD.TEMPLATE.SET_DEFAULT_TOOLTIP')}
                            </Tooltip>
                          }
                        >
                          <i className='bi bi-info-circle-fill text-gray-600 cursor-pointer interactive-icon'></i>
                        </OverlayTrigger>
                      </div>

                      {/* Action button */}
                      <span className='fs-7 mb-4 responsive-note'>
                        {t('MOBILE_WALLET_CARD.TEMPLATE.NOTE')}
                      </span>
                      <button
                        className='btn btn-primary interactive-button'
                        disabled={cardData?.status === 'draft'}
                        onClick={() => navigate(`/wallet-card/create?template=${cardId}`)}
                      >
                        {t('MOBILE_WALLET_CARD.TEMPLATE.BUTTON.CREATE_CARD', {
                          defaultValue: 'Create card',
                        })}
                      </button>
                    </div>
                  </Col>
                ) : (
                  <Col xs={12} lg={6} className={`d-flex flex-column align-items-center d-lg-flex ${isMobile ? 'd-none' : ''}`}>
                    {/* Stepper */}
                    <div className='w-100'></div>
                    <div className='d-flex flex-column mb-6 mb-md-8'>
                      <p className='text-start fs-1 fw-bold m-0 p-0 responsive-qr-title'>
                        {t('WALLET_CARD.DETAIL.TITLE')}
                      </p>
                      <p className='text-start fs-6 responsive-qr-description'>
                        {t('WALLET_CARD.DETAIL.DESCRIPTION')}
                      </p>
                    </div>
                    <div className='bg-light p-2 rounded-3 mb-6 mb-md-8 interactive-qr-container'>
                      <div ref={qrContainerRef} className='qr-code-wrapper'>
                        <QRCode
                          ref={qrRef}
                          size={window.innerWidth <= 768 ? 200 : 250}
                          value={qrCodeUrl}
                          fgColor='#000000'
                          bgColor='#ffffff'
                        />
                      </div>
                    </div>
                  </Col>
                )}
              </Row>
            </div>
          )}
        </Card.Body>
      </Card>
    </Container>
  )
}

export default WalletCardSuccessPage
